import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";

import { QrCodeStep, VerificationStep } from "./MfaEnrollmentSteps";

import { StepT, StepType } from "@/components/Stepper/Step/types";
import { Stepper } from "@/components/Stepper";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  useEnrollMfa,
  useCreateMfaChallenge,
  useVerifyMfaCode,
  type EnrollMfaResponse,
  type CreateMfaChallengeResponse,
  type EnrollMfaRequest,
} from "@/queries/auth/mfa";
import { displayErrorToast } from "@/utils/toast";
import { useDeleteMfaFactor } from "@/queries/auth/mfa/useDeleteMfaFactor";

interface MfaEnrollmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEnrollmentComplete?: () => void;
}

export const MfaEnrollmentDialog: React.FC<MfaEnrollmentDialogProps> = ({
  open,
  onOpenChange,
  onEnrollmentComplete,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [enrollmentData, setEnrollmentData] = useState<EnrollMfaResponse | null>(null);
  const [challengeData, setChallengeData] = useState<CreateMfaChallengeResponse | null>(null);
  const [verificationCode, setVerificationCode] = useState("");

  const form = useForm({
    defaultValues: {
      mfa_enrollment: "",
      mfa_verification: "",
    },
  });

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setEnrollmentData(null);
      setChallengeData(null);
      setVerificationCode("");
    }
  }, [open]);

  const { mutate: enrollMfa, isLoading: isEnrolling } = useEnrollMfa();
  const { mutate: createChallenge, isLoading: isCreatingChallenge } = useCreateMfaChallenge();
  const { mutate: verifyCode, isLoading: isVerifying } = useVerifyMfaCode();
  const { mutate: deleteMfa } = useDeleteMfaFactor();
  const queryClient = useQueryClient();

  const handleClose = (shouldDeleteMfa = false) => {
    setActiveStep(0);
    setEnrollmentData(null);
    setChallengeData(null);
    setVerificationCode("");
    onOpenChange(false);

    if (shouldDeleteMfa) {
      deleteMfa(undefined, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["user"] });
        },
      });
    }
  };

  useEffect(() => {
    if (!enrollmentData && open) {
      const enrollPayload: EnrollMfaRequest = { type: "totp" as const, issuer: "PromptLayer" };

      enrollMfa(enrollPayload, {
        onSuccess: data => {
          setEnrollmentData(data);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleNext = () => {
    if (activeStep === 0) {
      if (!enrollmentData) {
        displayErrorToast("Please wait for enrollment to complete");
        return;
      }

      createChallenge(undefined, {
        onSuccess: challengeResponse => {
          setChallengeData(challengeResponse);
          setActiveStep(1);
        },
      });
    } else if (activeStep === 1) {
      if (!challengeData || !verificationCode || verificationCode.length !== 6) {
        displayErrorToast("Please enter a valid 6-digit code");
        return;
      }

      verifyCode(
        {
          challenge_id: challengeData.challenge_id,
          code: verificationCode,
        },
        {
          onSuccess: data => {
            if (data.valid) {
              queryClient.invalidateQueries({ queryKey: ["user"] });
              queryClient.invalidateQueries({ queryKey: ["mfa", "policy"] });
              onEnrollmentComplete?.();
              handleClose();
            }
          },
        },
      );
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleNext();
  };

  const handleBack = () => {
    if (activeStep === 1) {
      setActiveStep(0);
      setChallengeData(null);
      setVerificationCode("");
    } else if (activeStep === 0) {
      handleClose(true);
    }
  };

  const steps = [
    {
      id: "mfa_enrollment",
      title: "Setting up Multi-Factor Authentication",
      description: enrollmentData
        ? "Scan the QR code with your authenticator app, then click continue"
        : "We'll generate a QR code for you to scan",
      type: StepType.CUSTOM_STEP,
      ignoreValidation: true,
      isNextDisabled: !enrollmentData,
      // eslint-disable-next-line react/no-unstable-nested-components
      getCustomContent: () => (
        <QrCodeStep enrollmentData={enrollmentData} isEnrolling={isEnrolling} />
      ),
    },
    {
      id: "mfa_verification",
      title: "Verify MFA Setup",
      description: "Enter the 6-digit code from your authenticator app to complete setup",
      type: StepType.CUSTOM_STEP,
      ignoreValidation: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      getCustomContent: () => (
        <VerificationStep
          verificationCode={verificationCode}
          setVerificationCode={setVerificationCode}
          isVerifying={isVerifying}
        />
      ),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={_ => handleClose(true)}>
      <DialogContent className="max-w-[480px]">
        <FormProvider {...form}>
          <form onSubmit={handleFormSubmit}>
            <Stepper
              size="dialog"
              activeStep={activeStep}
              steps={steps as unknown as StepT[]}
              onNext={handleNext}
              onBack={handleBack}
              onExit={() => handleClose(true)}
              isNextButtonLoading={
                (activeStep === 0 && (isEnrolling || isCreatingChallenge)) ||
                (activeStep === 1 && isVerifying)
              }
              isBackButtonVisible={activeStep > 0}
            />
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
